import { useState, useEffect, useCallback } from "react";
import { Divider, Modal, Spin, Switch } from "antd";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import HttpStatus from "../../../utility/status.enum";
import { fetchHostListing } from "../../../redux/manageSettings/actionCreator";
import Spinner from "../../common/Spinner";
import Notification from "../../../utility/notification";
import { Button } from "../../../components/buttons/buttons";
import { getKnowledeBaseDetails, updateKnowledgeBaseItemStatus } from "../../../redux/knowledgeBase/actionCreator";
import {
    QuestionCard,
    GroupHeaderRow,
    GroupTitleEditor,
    QuestionEditor,
    ListingTable,
} from "./GroupsManagerComponents";

const VISIBILITY_NUM_TO_LABEL = { 1: "all", 2: "past", 3: "current", 4: "future" };
const VISIBILITY_OPTIONS = ["all", "past", "current", "future"];
const VISIBILITY_LABEL_TO_NUM = { all: 1, past: 2, current: 3, future: 4 };

const parseVisibility = (raw) => {
    try {
        const parsed = JSON.parse(raw);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        return arr.map((n) => VISIBILITY_NUM_TO_LABEL[n]).filter(Boolean);
    } catch {
        return [];
    }
};

// ─── Read-only GroupSection for default_groups ────────────────────────────────

const ReadOnlyGroupSection = ({ group, listingLists, showHostComponent, backEndPrefix, showToggle = false, isEditMode = false, onToggle }) => {
    const [questions, setQuestions] = useState([]);
    const [sortedListingLists, setSortedListingLists] = useState([]);
    const [tempSelectedListings, setTempSelectedListings] = useState([]);
    const [isListingPopupOpen, setIsListingPopupOpen] = useState(false);

    useEffect(() => {
        const raw = group.questions || [];
        const parseListings = (listings) => {
            if (!listings) return [];
            if (typeof listings === "string") {
                try { const p = JSON.parse(listings); return Array.isArray(p) ? p : []; } catch { return []; }
            }
            return Array.isArray(listings) ? listings : [];
        };
        setQuestions(raw.map((q) => ({
            ...q,
            visibility: (() => {
                if (!q.content_visibility) return [];
                if (Array.isArray(q.content_visibility)) {
                    return q.content_visibility.map((n) => VISIBILITY_NUM_TO_LABEL[n]).filter(Boolean);
                }
                return parseVisibility(q.content_visibility);
            })(),
            listingSlugs: parseListings(q.listings).map(String),
            listing: q.listing_name || "",
        })));
    }, [group.questions]);

    const getListingLabel = (slugs = [], fallback = "") => {
        if (!slugs.length || !listingLists.length) return fallback;
        const selected = listingLists.filter((item) => slugs.includes(item.slug));
        if (!selected.length) return fallback;
        if (selected.length === 1) return selected[0]?.listing_name || fallback;
        return (
            <>
                <span>{selected[0]?.listing_name || "Selected listing"}</span>{" "}
                <a className="common_urlLink" href="#" onClick={(e) => {
                    e.preventDefault();
                    const sel = new Set(slugs);
                    const s = [], ns = [];
                    for (const item of listingLists) (sel.has(item.slug) ? s : ns).push(item);
                    setSortedListingLists([...s, ...ns]);
                    setTempSelectedListings([...slugs]);
                    setIsListingPopupOpen(true);
                }}>
                    & {selected.length - 1} more
                </a>
            </>
        );
    };

    return (
        <div>
            <GroupHeaderRow group={group} onEditGroup={() => {}} onDeleteGroup={() => {}} readOnly />
            <div className="ai-kb-group-questions">
                {questions.map((q, idx) => (
                    <QuestionCard
                        key={q.slug ?? `q-${idx}`}
                        question={q}
                        listingLabel={getListingLabel(q.listingSlugs, q.listing)}
                        onEdit={() => {}}
                        onDelete={() => {}}
                        isDeleting={false}
                        readOnly
                        showToggle={showToggle}
                        toggleAsSwitch={isEditMode}
                        onToggle={onToggle}
                    />
                ))}
            </div>
            {/* <Divider className="mt-20 mb-19" /> */}

            <Modal
                type="primary"
                open={isListingPopupOpen}
                footer={null}
                onCancel={() => setIsListingPopupOpen(false)}
                width="90%"
                maskClosable={false}
                aria-hidden="false"
                className="bulk-listing-modal template-config-listing-modal"
                title={`Selected Listings (${tempSelectedListings.length}/${listingLists?.length || 0})`}
            >
                <div className="guide-matching-listing">
                    <div className="project-modal">
                        <div className="guide-card">
                            <div className="guide-card-body">
                                <ListingTable
                                    sortedListingLists={sortedListingLists}
                                    tempSelectedListings={tempSelectedListings}
                                    onCheckboxChange={() => {}}
                                    backEndPrefix={backEndPrefix}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

// ─── KBDefaultsViewer ─────────────────────────────────────────────────────────

const KBDefaultsViewer = ({ contentType, cmsDefaultsText, cmsDefaultGroupsText, showToggle = false, isEditMode = false }) => {
    const { backEndPrefix, hostLoginRoleID } = global;
    const dispatch = useDispatch();
    const hostSlug = useParams()?.hostSlug ?? "";

    const [defaultItems, setDefaultItems] = useState([]);
    const [defaultGroups, setDefaultGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [listingLists, setListingLists] = useState([]);
    const [showHostComponent, setShowHostComponent] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const payload = { request_type: 1, content_type: contentType };
            if (hostSlug) payload.host_slug = hostSlug;
            const response = await dispatch(getKnowledeBaseDetails(payload));
            if (response?.status === HttpStatus.HTTP_OK) {
                const otherData = response?.other_data ?? {};
                setDefaultItems(Array.isArray(otherData.defaults) ? otherData.defaults : []);
                setDefaultGroups(Array.isArray(otherData.default_groups) ? otherData.default_groups : []);
            } else if (response) {
                Notification.addNotificationError(response?.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, contentType]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;
        if (user.role_id === hostLoginRoleID) {
            setShowHostComponent(true);
            dispatch(fetchHostListing({ host_slug: hostSlug })).then((res) => {
                if (res?.status === HttpStatus.HTTP_OK) setListingLists(res?.result || []);
            });
        }
    }, []);

    const handleToggle = async (slug, checked) => {
        if (!slug) return;
        setIsToggling(true);
        try {
            const response = await dispatch(updateKnowledgeBaseItemStatus(slug, { status_id: checked ? 1 : 2 }));
            if (response?.status === HttpStatus.HTTP_OK) {
                Notification.addNotificationSuccess(response?.message);
            } else {
                Notification.addNotificationError(response?.message);
            }
        } finally {
            setIsToggling(false);
        }
    };

    return (
        <div className="ai-kb-resources-manager">
            <Spinner loader={isLoading || isToggling} />
            {(defaultItems.length > 0 || defaultGroups.length > 0) && <Divider className="mt-20 mb-19" />}

            {/* Platform defaults (other_data.defaults) */}
            {defaultItems.length > 0 && (
                <>
                    {cmsDefaultsText && (
                        <div className="ai-kb-resource-header-copy mb-10">
                            <p className="project-detail-lable">{cmsDefaultsText}</p>
                        </div>
                    )}
                    {defaultItems.map((item, idx) => (
                        <QuestionCard
                            key={item.slug ?? `default-item-${idx}`}
                            question={{
                                ...item,
                                visibility: (() => {
                                    if (!item.content_visibility) return [];
                                    if (Array.isArray(item.content_visibility)) {
                                        return item.content_visibility.map((n) => VISIBILITY_NUM_TO_LABEL[n]).filter(Boolean);
                                    }
                                    try {
                                        const parsed = JSON.parse(item.content_visibility);
                                        return (Array.isArray(parsed) ? parsed : [parsed]).map((n) => VISIBILITY_NUM_TO_LABEL[n]).filter(Boolean);
                                    } catch { return []; }
                                })(),
                            }}
                            listingLabel=""
                            onEdit={() => {}}
                            onDelete={() => {}}
                            isDeleting={false}
                            readOnly
                            showToggle={showToggle}
                            toggleAsSwitch={isEditMode}
                            onToggle={handleToggle}
                        />
                    ))}
                </>
            )}

            {/* Divider between defaults and default_groups */}
            {defaultItems.length > 0 && defaultGroups.length > 0 && (
                <Divider className="mt-20 mb-19" />
            )}

            {/* Host default_groups (other_data.default_groups) */}
            {defaultGroups.length > 0 && (
                <>
                    {cmsDefaultGroupsText && (
                        <div className="ai-kb-resource-header-copy mb-10 mt-10">
                            <p className="project-detail-lable">{cmsDefaultGroupsText}</p>
                        </div>
                    )}
                    {defaultGroups.map((group, idx) => (
                        <ReadOnlyGroupSection
                            key={group.slug ?? `default-group-${idx}`}
                            group={group}
                            listingLists={listingLists}
                            showHostComponent={showHostComponent}
                            backEndPrefix={backEndPrefix}
                            showToggle={showToggle}
                            isEditMode={isEditMode}
                            onToggle={handleToggle}
                        />
                    ))}
                </>
            )}
        </div>
    );
};

export default KBDefaultsViewer;
